import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, tap, throwError } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { Actividad } from '@/app/models/interface/actividad.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { CapacitacionUsuario } from '@/app/models/interface/reportes';
import { CustomResponse } from '@/app/models/interface/response.model';
import { Rol } from '@/app/models/interface/rol.model';
import { Usuario } from '@/app/models/interface/usuario.model';

export interface ExtendedModel<T, G> extends PaginatorModel<T, G> {
  administrator: {
    active: number;
    inactive: number;
    deleted: number;
  };
}

@Injectable({ providedIn: 'root' })
export class UsuariosService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'usuarios/';

  findAll(params?: HttpParams) {
    return this.http
      .get<CustomResponse<ExtendedModel<Usuario[], HasPagination>>>(this.url, {
        params,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const usuarios = data.result;
          return {
            metadata: usuarios.metadata,
            data: usuarios.data.map((usuario) => {
              const actividades =
                usuario.actividadCompetenciaUsuario?.reduce(
                  (acc: Actividad[], curr) => {
                    // Busca si la actividad ya está en el array acumulador
                    const actividadExistente = acc.find(
                      (a) => a.id === curr.actividadId,
                    );

                    if (actividadExistente) {
                      // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
                      if (
                        !actividadExistente.competencias.some(
                          (c) => c.id === curr.competencia.id,
                        )
                      ) {
                        actividadExistente.competencias.push(curr.competencia);
                      }
                    } else {
                      // Si la actividad no existe, agrega una nueva entrada con sus competencias
                      acc.push({
                        ...curr.actividad,
                        competencias: [curr.competencia],
                      });
                    }

                    return acc;
                  },
                  [],
                ) || [];

              return {
                ...usuario,
                roles: usuario.usuarioRoles?.map((m) => m.rol) || [],
                subRoles: usuario.usuarioSubRoles?.map((m) => m.subRol) || [],
                actividades,
              };
            }),
          };
        }),
      );
  }

  findOne(id_usuario: number, anio?: number) {
    const url = this.url + id_usuario + '/' + (anio ?? '');
    return this.http.get<CustomResponse<Usuario>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const usuario = data.result;
        const actividades = usuario.actividadCompetenciaUsuario.reduce(
          (acc: Actividad[], curr) => {
            // Busca si la actividad ya está en el array acumulador
            const actividadExistente = acc.find(
              (a) => a.id === curr.actividadId,
            );

            if (actividadExistente) {
              // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
              if (
                !actividadExistente.competencias.some(
                  (c) => c.id === curr.competencia.id,
                )
              ) {
                actividadExistente.competencias.push(curr.competencia);
              }
            } else {
              // Si la actividad no existe, agrega una nueva entrada con sus competencias
              acc.push({
                ...curr.actividad,
                competencias: [curr.competencia],
              });
            }

            return acc;
          },
          [],
        );

        return {
          ...usuario,
          roles: usuario.usuarioRoles?.map((m) => m.rol) || [],
          subRoles: usuario.usuarioSubRoles?.map((m) => m.subRol) || [],
          actividades,
        };
      }),
    );
  }

  registerInitUser() {
    const url = environment.api + 'auth/registerInitUser';
    return this.http
      .get<CustomResponse<{ roles: Rol[]; actividades: Actividad[] }>>(url)
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const result = data.result;
          return {
            ...result,
            actividades: result.actividades.map((a) => {
              return {
                ...a,
                competencias: a.actividadCompetencia.map(
                  (ac) => ac.competencia,
                ),
              };
            }),
          };
        }),
      );
  }

  blanquearContrasenia(id_usuario: number) {
    const url = environment.api + 'auth/blanquearContrasenia/' + id_usuario;
    return this.http.patch<CustomResponse<boolean>>(url, {}).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  update(id: number, data: Partial<Usuario>) {
    const url = this.url + id;
    return this.http.patch<CustomResponse<Usuario>>(url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const usuario = data.result;
        return {
          ...usuario,
          roles: usuario.usuarioRoles?.map((m) => m.rol) || [],
          subRoles: usuario.usuarioSubRoles?.map((m) => m.subRol) || [],
        };
      }),
    );
  }

  createNotificacionCambioCompetencias(params: HttpParams) {
    const url = this.url + 'createNotificacionCambioCompetencias';
    return this.http
      .get(url, { params })
      .pipe(catchError((err) => handleError(err)));
  }

  upcomingExpirations(params: HttpParams) {
    const dir = this.url + 'upcomingExpirations';
    return this.http
      .get<CustomResponse<CapacitacionUsuario[]>>(dir, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  sendCredentials(id_usuario: number) {
    const direction = this.url + 'sendCredentials/' + id_usuario;
    return this.http
      .post<boolean>(direction, {})
      .pipe(catchError((err) => handleError(err)));
  }

  searchUsuario(params?: HttpParams) {
    const dir = this.url + 'search';
    return this.http
      .get<
        CustomResponse<{ nombre: string; id_usuario: number }[]>
      >(dir, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  reportExcelCapUsuario(params?: HttpParams) {
    const url = this.url + 'excelCapUsuario';

    return this.http.get(url, {
      params,
      responseType: 'blob',
    });
  }

  toggleTwoFactorAuth() {
    const url = this.url + 'toggleTwoFactorAuth';

    return this.http.patch(url, {}).pipe(
      catchError((err) => throwError(() => err)),
    );
  }
}
